import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiSearch, FiArrowRight, FiAlertCircle } from 'react-icons/fi';
import {
  Overlay,
  Sheet,
  Header,
  TitleRow,
  CloseBtn,
  SearchRow,
  SearchInput,
  Divider,
  SectionLabel,
  List,
  Item,
  Avatar,
  Arrow,
  Info,
  Name,
  Sub,
  Empty,
  Footer,
  CountLabel,
  ConfirmBtn,
} from './styles';

interface ICompany {
  id: string;
  nome_fantasia: string;
  cnpj?: string;
}

interface Props {
  companies: ICompany[];
  selectedId: string;
  onConfirm: (id: string, name: string) => void;
  onClose: () => void;
}

const AVATAR_COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#f59e0b',
  '#10b981',
  '#3b82f6',
  '#ef4444',
  '#14b8a6',
];

function getColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + hash * 31;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase();
}

function formatCnpj(cnpj?: string): string | null {
  if (!cnpj) return null;
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return digits.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    '$1.$2.$3/$4-$5',
  );
}

const CompanyPickerModal: React.FC<Props> = ({
  companies,
  selectedId,
  onConfirm,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  const [pendingId, setPendingId] = useState(selectedId);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = query.trim()
    ? companies.filter(
        c =>
          c.nome_fantasia.toLowerCase().includes(query.toLowerCase()) ||
          (c.cnpj && c.cnpj.includes(query)),
      )
    : companies;

  const handleConfirm = () => {
    const chosen = companies.find(c => c.id === pendingId);
    if (chosen) onConfirm(chosen.id, chosen.nome_fantasia);
    else onConfirm('', '');
    onClose();
  };

  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={e => e.stopPropagation()}>
        <Header>
          <TitleRow>
            <h3>Selecionar empresa</h3>
            <CloseBtn onClick={onClose} type="button">
              <FiX size={13} />
            </CloseBtn>
          </TitleRow>
          <SearchRow>
            <FiSearch size={15} />
            <SearchInput
              ref={inputRef}
              placeholder="Buscar por nome ou CNPJ..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </SearchRow>
        </Header>

        <Divider />
        <SectionLabel>
          {filtered.length}
          {filtered.length !== 1 ? ' empresas' : ' empresa'}
        </SectionLabel>

        <List>
          {filtered.length === 0 ? (
            <Empty>
              <FiAlertCircle size={26} />
              Nenhuma empresa encontrada
            </Empty>
          ) : (
            filtered.map(c => {
              const isSelected = c.id === pendingId;
              const cnpjFormatted = formatCnpj(c.cnpj);
              return (
                <Item
                  key={c.id}
                  $selected={isSelected}
                  onClick={() => setPendingId(isSelected ? '' : c.id)}
                >
                  <Avatar $color={getColor(c.nome_fantasia)}>
                    {getInitial(c.nome_fantasia)}
                  </Avatar>
                  <Info>
                    <Name>{c.nome_fantasia}</Name>
                    {cnpjFormatted && (
                      <Sub>
                        {'CNPJ '}
                        {cnpjFormatted}
                      </Sub>
                    )}
                  </Info>
                  <Arrow>
                    <FiArrowRight size={16} />
                  </Arrow>
                </Item>
              );
            })
          )}
        </List>

        <Footer>
          <CountLabel>
            {pendingId
              ? companies.find(c => c.id === pendingId)?.nome_fantasia
              : 'Nenhuma selecionada'}
          </CountLabel>
          <ConfirmBtn
            onClick={handleConfirm}
            type="button"
            disabled={!pendingId}
          >
            Confirmar
          </ConfirmBtn>
        </Footer>
      </Sheet>
    </Overlay>
  );
};

export default CompanyPickerModal;
